'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Card, CardContent, CardHeader } from '@eliadmin/core/src/components/ui/card';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import type { HTMLAttributes } from 'react';
import { useEffect, useRef, useState } from 'react';
import { PiDotOutlineFill } from 'react-icons/pi';
import { LuChevronDown, LuChevronLeft, LuChevronRight } from 'react-icons/lu';
type CategorySliderClassName = {
  main?: string;
  controlButton?: string;
  list?: string;
  listItem?: string;
  listItemImage?: string;
  listItemChevron?: string;
  link?: string;

  hoverCard?: string;
  hoverCardHeader?: string;
  hoverCardHeaderTitle?: string;
  hoverCardContent?: string;
  hoverCardList?: string;
  hoverCardListItem?: string;
  hoverCardLink?: string;
};

export interface CategorySliderProps {
  className?: CategorySliderClassName;
  style?: HTMLAttributes<HTMLElement>['style'];
  categories?: CategoriesEntity[];
  expandable?: boolean;
  showImage?: boolean;
}

export const CategorySlider = ({
  className,
  style,
  categories: propsCategories,
  expandable = true,
  showImage = true,
}: CategorySliderProps) => {
  const containerRef = useRef<HTMLUListElement | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<number | null>(null);
  const [showButtons, setShowButtons] = useState(false);

  const generalLayout = useGlobalStore(state => state.generalLayout);

  const scroll = (delta: number) => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const currentScrollLeft = container.scrollLeft;
    container.scrollTo({
      left: currentScrollLeft + delta,
      behavior: 'smooth',
    });
  };

  const handleCategoryHover = (index: number | null) => {
    setHoveredCategory(index);
  };

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        if (container.scrollWidth > container.clientWidth) {
          setShowButtons(true);
        } else {
          setShowButtons(false);
        }
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const categories = propsCategories;

  return (
    <nav
      className={cn('relative text-black', 'ea-category-slider__main', className?.main)}
      style={style}
    >
      {showButtons && (
        <LuChevronLeft
          onClick={() => scroll(-100)}
          className={cn(
            'mr-auto h-6 w-6 shrink-0 cursor-pointer',
            'ea-category-slider__control-button',
            className?.controlButton,
          )}
        />
      )}
      <ul
        ref={containerRef}
        className={cn(
          'hide-scroll-bar grid grid-cols-2 place-content-center gap-3 overflow-x-scroll will-change-scroll md:grid-cols-4 lg:grid-cols-6',
          'ea-category-slider__list',
          className?.list,
        )}
      >
        {categories?.map((category, index) => (
          <li
            key={index}
            className={cn(
              'flex w-full flex-row items-center justify-center bg-white ',
              'ea-category-slider__list-item',
              className?.listItem,
            )}
            onMouseEnter={() => {
              if (!category.childs?.length) return;
              if (!expandable) return;
              handleCategoryHover(index);
            }}
            onMouseLeave={() => {
              if (!category.childs?.length) return;
              if (!expandable) return;
              handleCategoryHover(null);
            }}
          >
            <Link prefetchOnHover
              href={category.slug}
              className={cn(
                'flex flex-col items-center gap-2 p-6 text-[14px] font-semibold hover:underline',
                'ea-category-slider__list-item-link',
                className?.link,
              )}
            >
              {category.image && showImage && (
                <Image
                  alt={resolveImageAlt(category?.image, category?.name, undefined)}
                  src={category.image.url}
                  width={0}
                  height={0}
                  sizes={'100vw'}
                  className={cn(
                    'h-10 w-10',
                    'ea-category-slider__list-item-image',
                    className?.listItemImage,
                  )}
                />
              )}
              <span className="text-center text-sm font-medium md:text-[17px]">
                {category.name}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {showButtons && (
        <LuChevronRight
          onClick={() => scroll(100)}
          className={cn(
            'ml-auto h-6 w-6 shrink-0 cursor-pointer',
            'ea-category-slider__control-button',
            className?.controlButton,
          )}
        />
      )}
    </nav>
  );
};
